/**
 * Política de contraseñas de la fuerza de venta — EP-CUDL-13.
 *
 * La regla es de 6 a 12 caracteres, con al menos una mayúscula y al menos un
 * carácter especial. Vive aquí, y no en el schema de un formulario, porque la
 * comparten el cambio obligatorio tras una contraseña temporal y el cambio
 * voluntario desde el menú, y porque el checklist en vivo necesita evaluar cada
 * regla por separado mientras el vendedor escribe.
 */

export const PASSWORD_MIN_LENGTH = 6;
export const PASSWORD_MAX_LENGTH = 12;

/**
 * Los 32 caracteres especiales de HU-CUDL-070 / CA-05, ni uno más. Las letras
 * acentuadas y la `ñ` no cuentan aquí: son letras, y suman a la regla de
 * mayúscula cuando van en mayúscula.
 */
export const SPECIAL_CHARACTERS = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';

/**
 * Se enumeran las mayúsculas de forma explícita en vez de usar una propiedad
 * Unicode: el teclado en español produce `Á É Í Ó Ú Ü Ñ`, y la lista literal
 * deja ver de un vistazo que están contempladas.
 */
const UPPERCASE_PATTERN = /[A-ZÁÉÍÓÚÜÑ]/;

export type PasswordRules = {
  length: boolean;
  uppercase: boolean;
  special: boolean;
};

/**
 * El campo impide teclear un carácter 13, así que en la práctica esta regla
 * solo puede fallar por abajo. El límite superior se comprueba igual porque un
 * pegado puede llegar por otro camino.
 */
export const hasValidLength = (value: string): boolean =>
  value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH;

export const hasUppercase = (value: string): boolean =>
  UPPERCASE_PATTERN.test(value);

export const hasSpecialCharacter = (value: string): boolean =>
  Array.from(value).some(character => SPECIAL_CHARACTERS.includes(character));

export const evaluatePassword = (value: string): PasswordRules => ({
  length: hasValidLength(value),
  uppercase: hasUppercase(value),
  special: hasSpecialCharacter(value),
});

export const isValidPassword = (value: string): boolean => {
  const rules = evaluatePassword(value);
  return rules.length && rules.uppercase && rules.special;
};

/**
 * Letras que el campo admite: las del alfabeto más las acentuadas y la eñe que
 * produce un teclado en español. Se enumeran en vez de usar una propiedad
 * Unicode para que la lista sea la misma que la de `UPPERCASE_PATTERN` y se
 * lean juntas.
 */
const LETTERS = 'A-Za-zÁÉÍÓÚÜÑáéíóúüñ';

/**
 * Escapa los caracteres con significado dentro de una clase de expresión
 * regular. Sin esto, el `-` de la lista de especiales abriría un rango y el
 * `]` la cerraría antes de tiempo.
 */
const ALLOWED_CHARACTER = new RegExp(
  `^[${LETTERS}0-9${SPECIAL_CHARACTERS.replace(/[\\\]^-]/g, '\\$&')}]$`,
);

/**
 * Descarta todo lo que la política no admite: se queda con letras, dígitos y
 * los 32 caracteres especiales, y tira el resto —espacios y emoji, sobre todo—.
 *
 * Filtra carácter por carácter en vez de rechazar el valor completo: al pegar
 * una contraseña con un espacio al final, lo que el vendedor espera es que
 * entre sin el espacio, no que no entre nada. Un espacio invisible al pegar es
 * el hallazgo F, y ahí cuesta un intento de ingreso.
 *
 * Se recorre con `Array.from` y no con un `replace`: un emoji ocupa dos
 * unidades de código, y así se elimina entero en vez de dejar la mitad.
 */
export const filterPasswordCharacters = (value: string): string =>
  Array.from(value)
    .filter(character => ALLOWED_CHARACTER.test(character))
    .join('');
